import { useMemo } from 'react';
import { SelectionDto } from '../api/sbapi/sbapi-api-model';
import { SELECTION_OUTCOME_TYPE_SORT_MAP_DEFAULT, SelectionOutcomeTypeSortMap } from '../sportsbook-constants';
import localeCompare from '../../../../utils/localeCompare';

function sortSelectionsByOrder(selection1: SelectionDto, selection2: SelectionDto) {
	if (selection1.order && selection2.order) {
		if (selection1.order < selection2.order) {
			return -1;
		}
		if (selection1.order > selection2.order) {
			return 1;
		}
	}

	return 0;
}

function sortSelectionsByOutcomeTypeOrder(selection1: SelectionDto, selection2: SelectionDto) {
	const selection1OutcomeTypeOrder =
		SelectionOutcomeTypeSortMap[selection1.outcomeType ?? SELECTION_OUTCOME_TYPE_SORT_MAP_DEFAULT];

	const selection2OutcomeTypeOrder =
		SelectionOutcomeTypeSortMap[selection2.outcomeType ?? SELECTION_OUTCOME_TYPE_SORT_MAP_DEFAULT];

	if (selection1OutcomeTypeOrder < selection2OutcomeTypeOrder) {
		return -1;
	}

	if (selection1OutcomeTypeOrder > selection2OutcomeTypeOrder) {
		return 1;
	}

	return 0;
}

function sortSelectionsByPoints(selection1: SelectionDto, selection2: SelectionDto) {
	if (selection1.points && selection2.points) {
		if (selection1.points < selection2.points) {
			return -1;
		}
		if (selection1.points > selection2.points) {
			return 1;
		}
	}

	return 0;
}

export function sortSelectionsByOdds(selection1: SelectionDto, selection2: SelectionDto) {
	if (selection1.trueOdds && selection2.trueOdds) {
		if (selection1.trueOdds < selection2.trueOdds) {
			return -1;
		}
		if (selection1.trueOdds > selection2.trueOdds) {
			return 1;
		}
	}

	return 0;
}

function sortSelectionsByName(selection1: SelectionDto, selection2: SelectionDto) {
	const selection1Name = selection1.name.trim().toLowerCase() ?? '';
	const selection2Name = selection2.name.trim().toLowerCase() ?? '';
	return localeCompare(selection1Name, selection2Name);
}

export enum SortSelectionsBy {
	Order,
	OutcomeTypeOrder,
	Points,
	Name,
	Odds
}

export function sortSelections(selection1: SelectionDto, selection2: SelectionDto, sortOrder: SortSelectionsBy[]) {
	// eslint-disable-next-line no-restricted-syntax
	for (const order of sortOrder) {
		let orderResult = 0;

		switch (order) {
			case SortSelectionsBy.Order:
				orderResult = sortSelectionsByOrder(selection1, selection2);
				break;
			case SortSelectionsBy.OutcomeTypeOrder:
				orderResult = sortSelectionsByOutcomeTypeOrder(selection1, selection2);
				break;
			case SortSelectionsBy.Points:
				orderResult = sortSelectionsByPoints(selection1, selection2);
				break;
			case SortSelectionsBy.Name:
				orderResult = sortSelectionsByName(selection1, selection2);
				break;
			case SortSelectionsBy.Odds:
				orderResult = sortSelectionsByOdds(selection1, selection2);
				break;
			default:
				break;
		}

		if (orderResult !== 0) {
			return orderResult;
		}
	}

	return 0;
}

/**
 * This method takes in an array of selections and tries to determine the correct order
 * If the selections dont have a specific order it will try to group selections by outcome type
 */
function useSortedSelections(
	selections: SelectionDto[],
	order: SortSelectionsBy[] = [
		SortSelectionsBy.Order,
		SortSelectionsBy.OutcomeTypeOrder,
		SortSelectionsBy.Points,
		SortSelectionsBy.Name
	]
) {
	const sortedSelections = useMemo(() => {
		return selections
			.slice()
			.sort((selection1: SelectionDto, selection2: SelectionDto) =>
				sortSelections(selection1, selection2, order)
			);
	}, [order, selections]);

	return { sortedSelections };
}

export default useSortedSelections;
